import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Combines class names using clsx and tailwind-merge
 * This is the standard utility function used by shadcn/ui components
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number as Indian Rupees (lakhs/crores format)
 * e.g., 1234567 -> "₹12,34,567"
 */
export function formatINR(amount: number): string {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  })
  return formatter.format(amount)
}

/**
 * Format a number as USD
 * e.g., 1234567 -> "$1,234,567"
 */
export function formatUSD(amount: number): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
  return formatter.format(amount)
}

/**
 * Format large numbers in lakhs/crores notation
 * e.g., 10000000 -> "1 Cr", 100000 -> "1 L"
 */
export function formatINRCompact(amount: number): string {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)} Cr`
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)} L`
  }
  return formatINR(amount)
}

/**
 * Format large numbers in K/M/B notation
 * e.g., 1000000 -> "$1M"
 */
export function formatUSDCompact(amount: number): string {
  if (amount >= 1000000000) {
    return `$${(amount / 1000000000).toFixed(1)}B`
  }
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`
  }
  return formatUSD(amount)
}

/**
 * Convert USD to INR
 */
export function convertToINR(usd: number, exchangeRate: number): number {
  return usd * exchangeRate
}

/**
 * Convert INR to USD
 */
export function convertToUSD(inr: number, exchangeRate: number): number {
  return inr / exchangeRate
}

/**
 * Format a percentage
 * e.g., 0.0425 -> "4.25%"
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Debounce function for live preview updates
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  
  return function executedFunction(...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => {
      func(...args)
    }, wait)
  }
}

/**
 * Calculate years until target date
 */
export function yearsUntil(targetAge: number, currentAge: number): number {
  return Math.max(0, targetAge - currentAge)
}

/**
 * Check if a value is within a range
 */
export function isInRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max
}
